import { useEffect, useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  getFilterLabel,
  setFilterLabels,
} from '@/components/data-table/filters-v2/store/filter-labels-store';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { DEFAULT_FILTERS_PAGE_SIZE } from '@/lib/constants/pagination';
import { cn } from '@/lib/utils';
import { GetEntitiesParams } from '@/modules/onboarding/common/types';
import { DynamicAutocompleteFilter, FilterOption } from '@/types/filters';

interface IProps<TEntity = object> {
  config: DynamicAutocompleteFilter<TEntity>;
  value: string[];
  onChange: (key: string, value: string[]) => void;
  appliedFilters?: Record<string, string | string[]>;
}

export const FilterDynamicAutocomplete = <TEntity,>({
  config,
  value,
  onChange,
  appliedFilters = {},
}: IProps<TEntity>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<FilterOption[]>([]);

  const selected = useMemo(() => new Set(value), [value]);

  // Get dependent filter value if this filter depends on another.
  // Treat empty arrays as undefined so dependent filters load all results when dependency is cleared.
  const rawDependentValue = config.dependsOn ? appliedFilters[config.dependsOn] : undefined;
  const dependentValue =
    Array.isArray(rawDependentValue) && rawDependentValue.length === 0
      ? undefined
      : rawDependentValue;

  const debouncedSetSearch = useDebouncedCallback((val: string) => {
    setSearch(val);
  }, 300);

  const handleSearch = (val: string) => {
    setSearchValue(val);
    debouncedSetSearch(val);
  };

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useInfiniteQuery({
    queryKey: ['filter-entities', config.name, search, dependentValue],
    enabled: open && !!config.getEntities,
    queryFn: async ({ pageParam = undefined }) => {
      if (!config.getEntities) return { items: [], pagination: { total: 0, cursor: null } };
      const params: GetEntitiesParams = {
        searchString: search,
        cursor: pageParam as number | undefined,
        pageSize: DEFAULT_FILTERS_PAGE_SIZE,
        ...(config.dependsOn && dependentValue ? { [config.dependsOn]: dependentValue } : {}),
      };
      return config.getEntities(params);
    },
    getNextPageParam: lastPage => lastPage.pagination.cursor,
    initialPageParam: null,
    staleTime: 60 * 60 * 1000, // Cache for 60 minutes
    gcTime: 60 * 60 * 1000, // Keep in cache for 60 minutes
  });

  const { setContainerElement, setSentinelElement } = useInfiniteScroll({
    hasNextPage,
    isFetching,
    onLoadMore: fetchNextPage,
  });

  const options: FilterOption[] = useMemo(() => {
    if (!data) return [];
    const mappedOptions = data.pages.flatMap(page =>
      page.items.map((entity: TEntity) => config.mapEntityToOption(entity))
    );

    // Cache labels for future use (e.g., after page reload)
    if (mappedOptions.length > 0) {
      setFilterLabels(config.name, mappedOptions);
    }

    return mappedOptions;
  }, [data, config.mapEntityToOption, config.name]);

  const toggleOption = (val: string) => {
    const isSelected = selected.has(val);
    const opt = options.find(o => o.value === val);

    let newSelectedOptions: FilterOption[];
    if (isSelected) {
      newSelectedOptions = selectedOptions.filter(o => o.value !== val);
    } else {
      newSelectedOptions = opt
        ? [...selectedOptions.filter(o => o.value !== val), opt]
        : selectedOptions;
    }

    setSelectedOptions(newSelectedOptions);
    onChange(
      config.name,
      newSelectedOptions.map(o => o.value)
    );
  };

  const onClear = () => {
    setSelectedOptions([]);
    onChange(config.name, []);
  };

  const renderOptionLabel = (opt: FilterOption) =>
    config.CustomLabelComponent ? (
      config.CustomLabelComponent(opt.label, opt)
    ) : (
      <div className="flex items-center gap-2">
        <span className="text-sm">{opt.label}</span>
      </div>
    );

  const label = useMemo(
    () => (selectedOptions.length > 0 ? `: ${selectedOptions.map(o => o.label).join(', ')}` : ''),
    [selectedOptions]
  );

  // Keep selectedOptions in sync with value
  useEffect(() => {
    if (options.length === 0 && value.length === 0) {
      setSelectedOptions([]);
      return;
    }

    const resolved = value
      .map(v => {
        // Try to find in loaded options first
        const fromOptions = options.find(o => o.value === v);
        if (fromOptions) return fromOptions;

        // If not in options, try to find in current selectedOptions
        const fromSelected = selectedOptions.find(o => o.value === v);
        if (fromSelected) return fromSelected;

        // If still not found, try to get from cached labels (for page reload scenario)
        const cachedLabel = getFilterLabel(config.name, v);
        if (cachedLabel) {
          return { value: v, label: cachedLabel };
        }

        // Last resort: use value as label (will be replaced when data loads)
        return { value: v, label: v };
      })
      .filter(Boolean) as FilterOption[];

    setSelectedOptions(resolved);
  }, [value, options, config.name]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn('h-9 px-2 py-1 border rounded text-sm text-left', open && 'bg-muted')}
      >
        <div className="flex items-center justify-between gap-2">
          <span>
            {config.label}
            <span className="font-medium">{label}</span>
          </span>
          <ChevronDown className={cn('transition', open && 'rotate-180')} size={16} />
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-0" side="bottom" align="start">
        <Command shouldFilter={false}>
          {config.withSearch && (
            <CommandInput
              placeholder={t('common:filters.search', { value: config.label })}
              className="h-9"
              onValueChange={handleSearch}
              value={searchValue}
            />
          )}

          <CommandList className="max-h-60 overflow-auto" ref={setContainerElement}>
            {options.map(opt => {
              const isSelected = selected.has(opt.value);
              return (
                <CommandItem
                  key={opt.value}
                  onSelect={() => toggleOption(opt.value)}
                  className={cn(
                    'flex min-w-0 items-center cursor-pointer overflow-hidden',
                    isSelected && 'bg-muted'
                  )}
                >
                  <Checkbox checked={isSelected} id={opt.value} className="shrink-0" />
                  <Label className="flex min-w-0 flex-1 overflow-hidden">
                    {renderOptionLabel(opt)}
                  </Label>
                </CommandItem>
              );
            })}

            {isLoading || isFetching ? (
              <p className="p-2 text-sm">{t('common:loading')}</p>
            ) : options.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">{t('common:noResultsFound')}</p>
            ) : null}

            {/* Sentinel element for observer */}
            {hasNextPage && <div ref={setSentinelElement} className="h-6" />}
          </CommandList>

          <CommandSeparator />
          <CommandGroup className="p-2">
            <div className="flex items-center justify-between">
              <Button size="sm" className="h-8" onClick={onClear} disabled={selected.size === 0}>
                {t('common:filters.clearAll')}
              </Button>
              <p className="text-sm text-muted-foreground">
                {t('common:filters.selected', { count: selected.size })}
              </p>
            </div>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
